import React from 'react';
import _ from 'lodash';
import Helmet from 'react-helmet';
import moment from 'moment';

import FieldApi from '../services/field/FieldApi';
import FieldCcusApi from '../services/FieldCcus';
import * as FieldTreeApi from '../services/FieldTree';
import { CompanyService } from '../services/Companies';
import _masterSlideLoader from '../utilities/masterSlideLoader';
import {
  DATE_TIME_FORMAT,
  HEADER_CONSTANTS,
  PAGE_REFERER,
  STATUS_CODE,
  TIME_OUT_TWO_SECOND,
} from '../utilities/constants';
import { Description } from '../components/Description';
import { MasterPageHeader } from '../components/PageHeader';
import {
  FormElement,
  FormList,
  FormListItem,
  FormListSubHeader,
} from '../components/CommonFormComponents';
import { FieldNaviList } from '../components/list/FieldNaviList';
import Switch from '../components/Switch';
import { FormInformationDialog } from '../components/FormInformationDialog';
import {
  CcusCooperationConfirmDialog,
  CcusCooperationErrorDialog,
} from '../components/modals/FieldCcusCooperationDialog';
import { BackgroundLayer } from '../components/modals/BackgroundLayer';
import { CcusConnectingDialog } from '../components/modals/CcusConnectingDialog';
import _dialogLoader from '../utilities/dialogLoader';
import { messageUtility } from '../utilities/messageUtility';
import { isApiFlagOn, setWaitTime } from '../utilities/commonUtil';
import SessionStorageService from '../services/utilities/SessionStorageService';
import { Confirmation } from '../components/modals/common';

export class FieldCcusCooperation extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      ccusId: null,
      fieldId: null,
      fieldName: null,
      fieldAddress: null,
      ccusFieldTreeAutoTask: null,
      autoTaskCcusActive: null,
      isFieldCompleted: false,
      isLoadingCcusStatus: true,
      isConnectingCcus: false,
      isShowStopCcusConfirmDialog: false,
      beforePageReferer: null,
      ccusSecededFlag: false,
    };

    this.state.beforePageReferer = SessionStorageService.getBeforePageName();

    // slideLoaderからの情報を取得
    if (props.view) {
      this.fieldId = props.view.state.id;
    } else if (props.match) {
      this.fieldId = props.match.params.id;
    }
    this.callApiFlag = false;
  }

  componentDidMount() {
    this.onLoad();
  }

  /**
   * handle load data from api in page
   */
  onLoad = () => {
    FieldCcusApi.checkPermissionFieldTreeCooperationWithCcus(this.fieldId).then(
      response => {
        !_.get(response, 'data.entities.enable_ccus_flag') &&
          window.location.replace('/error-permission');
      }
    );
    Promise.all([
      FieldApi.loadField(this.fieldId),
      FieldTreeApi.fetchCcusFieldTreeState(this.fieldId),
      CompanyService.getCompaniesCcusSecededFlag(),
    ])
      .then(data => {
        const [fieldData, ccusInfo, ccusSecededFlagData] = data;
        const { field } = _.get(fieldData, 'data.entities');
        const ccusSecededFlag = _.get(ccusSecededFlagData, 'data.entities.ccus_seceded_flag');
        if (field) {
          const { ccus_site_id, id, name } = field;
          const isFieldCompleted = this.checkFieldCompleted(field);
          const ccusFieldTreeAutoTask = _.get(
            ccusInfo,
            'data.ccus_field_tree_auto_task'
          );
          let autoTaskCcusActive =
            ccusFieldTreeAutoTask && ccusFieldTreeAutoTask.active;
          if (isFieldCompleted && !_.isNil(ccusFieldTreeAutoTask)) {
            autoTaskCcusActive = false;
          }
          this.setState({
            ccusId: ccus_site_id,
            fieldId: id,
            fieldName: name,
            fieldAddress: this.getFieldAddress(field),
            isFieldCompleted,
            ccusFieldTreeAutoTask,
            autoTaskCcusActive,
            ccusSecededFlag
          });
        }
      })
      .finally(() => {
        this.setState({ isLoadingCcusStatus: false });
      });
  };

  /**
   * check the completed status of fields
   * @param complete_schedule_date complete day of field
   * @returns {boolean}
   */
  checkFieldCompleted = ({ complete_schedule_date }) => {
    const completeScheduleDate = moment(
      complete_schedule_date,
      DATE_TIME_FORMAT.slashDateFormat
    );
    return completeScheduleDate.isBefore(moment(), 'day');
  };

  /**
   * get field address from field data
   * @param field field data
   * @returns {string}
   */
  getFieldAddress = field => {
    const {
      address_prefecture,
      address_city,
      address_add_1,
      address_add_2,
    } = field;
    return `${address_prefecture || ''}${address_city || ''}${address_add_1 ||
      ''}${address_add_2 || ''}`;
  };

  /**
   * get field from ccus field data
   */
  getCcusFieldData = () => {
    if (this.state.isConnectingCcus) {
      return;
    }
    this.setState({ isConnectingCcus: true }, () => {
      const state = _.cloneDeep(this.state);
      FieldCcusApi.beginFetchCcusFieldData({
        field_id: this.state.fieldId,
        ccus_field_id: this.state.ccusId,
        from_ccus_cooporation_page: true,
      })
        .then(response => {
          if (response.result) {
            this.monitoringFetchCcusFieldData(
              response.data.entities.ccus_state_management_id,
              response.data.entities.ccus_field_id
            );
          } else {
            this.showCcusErrorDialog(response);
            state.isConnectingCcus = false;
          }
        })
        .catch(error => {
          this.showCcusErrorDialog(error);
          state.isConnectingCcus = false;
        })
        .finally(() => {
          this.setState(state);
        });
    });
  };

  /**
   * 現場のCCUS連携状況を停止する
   */
  stopCcusFieldConnection = () => {
    const state = _.cloneDeep(this.state);

    const param = {
      field_id: state.fieldId,
      auto_task_id:
        state.ccusFieldTreeAutoTask && state.ccusFieldTreeAutoTask.id,
      lock_version:
        state.ccusFieldTreeAutoTask && state.ccusFieldTreeAutoTask.lock_version,
    };
    FieldCcusApi.stopFieldConnectionStatus(param)
      .then(response => {
        if (response.result) {
          state.ccusFieldTreeAutoTask =
            response.data.entities.ccus_field_tree_auto_task;
          state.autoTaskCcusActive = state.ccusFieldTreeAutoTask.active;
        } else {
          this.handleError(response);
        }
        state.isShowStopCcusConfirmDialog = false;
      })
      .catch(error => {
        this.handleError(error);
        state.isShowStopCcusConfirmDialog = false;
      })
      .finally(() => {
        this.setState(state);
      });
  };

  /**
   * move to page slider
   */
  moveToBeforePage = () => {
    _masterSlideLoader.show('Fields', {
      state: {},
    });
  };

  /**
   * handle show cooperation confirm dialog and callback
   */
  showCooperationConfirmDialog = () => {
    _dialogLoader.show(CcusCooperationConfirmDialog, {
      callback: {
        onConfirm: () => {
          this.cooperateWithCcus();
        },
        onCancel: () => {
          this.setState({ isConnectingCcus: false }, () => {
            _dialogLoader.close();
          });
        },
      },
    });
  };

  /**
   * handle show cooperation information dialog and callback
   */
  showCooperationErrorDialog = () => {
    _dialogLoader.show(CcusCooperationErrorDialog, {
      callback: {
        onCancel: () => {
          this.setState({ isConnectingCcus: false }, () => {
            _dialogLoader.close();
          });
        },
      },
    });
  };

  /**
   * CCUS連携通信中ダイアログ
   */
  showCcusConnectingDialog = () => {
    return this.state.isConnectingCcus ? (
      <BackgroundLayer modalRoot='root' modalState={true}>
        <CcusConnectingDialog />
      </BackgroundLayer>
    ) : null;
  };

  /**
   * CCUSエラー(CCUS通信エラー,CCUS現場情報取得エラー)ダイアログを表示
   *
   * @param {string} response responseデータ
   */
  showCcusErrorDialog = response => {
    let message = '';
    if (_.isEmpty(response.message)) {
      if (response.data) {
        message = response.data.message;
      }
    } else {
      message = response.message;
    }

    _dialogLoader.show(FormInformationDialog, {
      title: messageUtility.getMessage('E-001-402'),
      message,
      onClose: () => {
        _dialogLoader.close();
        window.location.reload();
      },
    });
  };

  /**
   * CCUS現場情報取得状況を監視する
   * @param {number} ccusStateManagementId CCUSステータス管理ID
   * @param {string} ccusFieldId CCUS現場ID
   * */
  monitoringFetchCcusFieldData = (ccusStateManagementId, ccusFieldId) => {
    setWaitTime(TIME_OUT_TWO_SECOND).then(() => {
      const params = {
        ccus_state_management_id: ccusStateManagementId,
        ccus_field_id: ccusFieldId,
      };
      const state = _.cloneDeep(this.state);
      FieldCcusApi.fetchCcusFieldData(params)
        .then(response => {
          if (response.result) {
            if (response.status === STATUS_CODE.ok) {
              // ステータスコードが正常(200)の場合
              const { api_use_flg } = response.data.entities.ccus_field;
              if (isApiFlagOn(api_use_flg)) {
                this.showCooperationConfirmDialog();
              } else {
                this.showCooperationErrorDialog();
              }
            } else if (response.status === STATUS_CODE.accepted) {
              // ステータスコードが処理中(202)の場合
              // 再度現場情報取得状況を監視する
              this.monitoringFetchCcusFieldData(
                ccusStateManagementId,
                ccusFieldId
              );
            } else {
              this.showCcusErrorDialog(response);
              state.isConnectingCcus = false;
            }
          } else {
            this.showCcusErrorDialog(response);
            state.isConnectingCcus = false;
          }
        })
        .catch(error => {
          this.showCcusErrorDialog(error);
          state.isConnectingCcus = false;
        })
        .finally(() => {
          this.setState(state);
        });
    });
  };

  /**
   * handle cooperate field tree with ccus
   */
  cooperateWithCcus = () => {
    if (this.callApiFlag) {
      return;
    }
    this.callApiFlag = true;

    const { fieldId, ccusFieldTreeAutoTask } = this.state;
    const param = {
      field_id: fieldId,
      auto_task_id: ccusFieldTreeAutoTask ? ccusFieldTreeAutoTask.id : '',
      lock_version: ccusFieldTreeAutoTask
        ? ccusFieldTreeAutoTask.lock_version
        : '',
    };

    FieldCcusApi.cooperatFieldTreeWithCcus(param)
      .then(response => {
        if (!response.result) {
          this.handleError(response);
          return;
        }
        _dialogLoader.close();
        const ccusFieldTreeAutoTask =
          response.data.entities.ccus_field_tree_auto_task;
        this.setState({
          autoTaskCcusActive: ccusFieldTreeAutoTask.active,
          ccusFieldTreeAutoTask,
        });
      })
      .finally(() => {
        this.setState({ isConnectingCcus: false }, () => {
          this.callApiFlag = false;
        });
      });
  };

  /**
   * handle update status auto task of field tree with ccus
   */
  toggleAutoTaskCcus = () => {
    if (this.state.isFieldCompleted) {
      return;
    }
    if (this.callApiFlag) {
      return;
    }
    this.callApiFlag = true;
    const { autoTaskCcusActive } = this.state;
    const apiFunction = !autoTaskCcusActive
      ? FieldTreeApi.ccusFieldTreeStart
      : FieldTreeApi.ccusFieldTreeStop;
    apiFunction(this.fieldId)
      .then(res => {
        if (!res.result) {
          this.handleError(res);
          return;
        }
        const ccusFieldTreeAutoTask =
          res.data.entities.ccus_field_tree_auto_task;
        this.setState({
          autoTaskCcusActive: ccusFieldTreeAutoTask.active,
          ccusFieldTreeAutoTask,
        });
      })
      .finally(() => {
        this.callApiFlag = false;
      });
  };

  /**
   * handle error of api response
   * @param error
   */
  handleError = error => {
    const commonErrorMessage = messageUtility.getMessage('E-000-055');
    const message = _.isEmpty(_.get(error, 'data.message'))
      ? commonErrorMessage
      : error.data.message;
    const title = messageUtility.getMessage('E-001-402');
    _dialogLoader.show(FormInformationDialog, {
      title,
      message,
      onClose: () => {
        _dialogLoader.close();
        window.location.reload();
      },
    });
  };

  /**
   * get cooperation status string with ccus of field tree
   * @returns {string}
   */
  getCooperationStatusString = () =>
    _.isNil(this.state.ccusFieldTreeAutoTask)
      ? '未連携'
      : this.state.ccusFieldTreeAutoTask.connection_status
      ? '連携開始済み'
      : '停止中';

  render() {
    const {
      ccusId,
      ccusFieldTreeAutoTask,
      autoTaskCcusActive,
      isFieldCompleted,
      isLoadingCcusStatus,
      isConnectingCcus,
      ccusSecededFlag,
    } = this.state;

    return (
      <div
        id='slide-view'
        className='contruction-slide-view show FieldCcusCooperation'
      >
        <Helmet>
          <title>{HEADER_CONSTANTS.title} - 現場詳細</title>
        </Helmet>

        <div className='contruction-slide-view__view'>
          <MasterPageHeader title='現場詳細' onClose={this.moveToBeforePage} />

          <div className='contruction-slide-view__body contruction-slide-view__body--height-fix'>
            <div className='contruction-detail-view'>
              <div className='contruction-detail-view__sidemenu'>
                <FieldNaviList
                  fieldId={this.fieldId}
                  selectedTab='FieldCcusCooperation'
                  showJvSettingTab={
                    this.state.beforePageReferer === PAGE_REFERER.fields.key
                      ? true
                      : false
                  }
                />
              </div>

              <div className='contruction-detail-view__body contruction-detail-view__body--fixed'>
                <div className='contruction-detail-view__menu'>
                  <button
                    className='btn-text--sm'
                    onClick={this.moveToBeforePage}
                  >
                    <i className='i-back--blue i-2x vertical-middle' />
                    現場一覧
                  </button>
                </div>
                <div className='contruction-detail-view__bar' />
                <div className='contruction-detail-view__main contruction-detail-view__main--fixed'>
                  <FormList>
                    <FormListSubHeader title='現場情報' noMarginTop={true} />
                    <FormListItem label='CCUS現場ID'>
                      <FormElement>{ccusId}</FormElement>
                    </FormListItem>
                    <FormListItem label='現場名'>
                      <FormElement>{this.state.fieldName}</FormElement>
                    </FormListItem>
                    <FormListItem label='現場住所'>
                      <FormElement>{this.state.fieldAddress}</FormElement>
                    </FormListItem>
                    <FormListSubHeader title='CCUSとの施工体制連携' />
                    <div className='cooperation-description'>
                      <Description>
                        CCUSとの施工体制連携を開始すると、施工体制情報と施工体制作業員情報がBuildeeからCCUSに連携されます。
                        <br />
                        <br />
                        【注意事項】
                        <ul className='cooperation-notes'>
                          <li>
                            <span className='alert-text'>
                              CCUSで登録した施工体制情報を事前に削除いただく必要があります。
                            </span>
                            <br />
                            施工体制情報が登録された状態で連携を開始するとエラーが発生します。
                          </li>
                          <li>
                            CCUSの現場契約情報で「標準APIの利用」項目を「利用する」に設定する必要があります。
                            <br />
                            「利用する」に設定されていない場合、連携が開始できません。
                          </li>
                          <li>
                            CCUSの詳しい操作については、
                            <a
                              className='link-text'
                              rel='noreferrer noopener'
                              target='_blank'
                              href='https://secure.okbiz.jp/faq-ccus/faq/show/983?site_domain=default'
                            >
                              こちら
                            </a>
                            をご確認ください。
                          </li>
                          <li>
                            連携状況を「停止」にしても入退場実績の送信は行われます。
                            <br />
                            入退場実績の送信を停止したい場合には、CCUS現場IDを削除してください。
                          </li>
                        </ul>
                      </Description>
                    </div>
                    <FormListItem label='連携状況'>
                      <FormElement>
                        {!isLoadingCcusStatus && !ccusSecededFlag && (
                          <React.Fragment>
                            <span>{this.getCooperationStatusString()}</span>
                            {_.isNil(ccusFieldTreeAutoTask) && (
                              <button
                                className='btn--sub btn-cooperate'
                                onClick={this.getCcusFieldData}
                                disabled={isConnectingCcus}
                              >
                                開始
                              </button>
                            )}
                            {!_.isNil(ccusFieldTreeAutoTask) &&
                              (!ccusFieldTreeAutoTask.connection_status ? (
                                <button
                                  className={'btn--sub btn-cooperate '}
                                  onClick={this.getCcusFieldData}
                                  disabled={isConnectingCcus}
                                >
                                  開始
                                </button>
                              ) : (
                                <button
                                  className='btn--negative btn-cooperate'
                                  onClick={() => {
                                    this.setState({
                                      isShowStopCcusConfirmDialog: true,
                                    });
                                  }}
                                >
                                  停止
                                </button>
                              ))}
                          </React.Fragment>
                        )}
                        {ccusSecededFlag && (
                          <span className='text--negative'>
                            {messageUtility.getMessage('I-000-244')}
                          </span>
                        )}
                      </FormElement>
                    </FormListItem>
                    {!_.isNil(ccusFieldTreeAutoTask) &&
                      ccusFieldTreeAutoTask.connection_status &&
                      !ccusSecededFlag && (
                        <FormListItem label='施工体制の夜間自動連携'>
                          <FormElement>
                            <div
                              className={
                                isFieldCompleted ? 'switch-disabled' : ''
                              }
                            >
                              <Switch
                                switchTextArray={['ON', 'OFF']}
                                handleToggle={() => this.toggleAutoTaskCcus()}
                                checked={
                                  autoTaskCcusActive && !isFieldCompleted
                                }
                                disabled={isFieldCompleted}
                              />
                            </div>
                            <div className='auto-cooperation-info'>
                              自動連携をONにすると毎日夜間（※1）に施工体制情報をCCUSに自動で連携します。（推奨）現場の竣工予定日をすぎている場合は自動連携がOFFとなりますが、通常は日々施工体制情報の連携が必要になるためOFFに変更いただく必要はありません。
                              <br />
                              自動連携がOFFの場合は、現場体制（施工体系図）画面より「今すぐCCUSに連携」をクリックし手動で連携を行ってください。（※2）
                              <br />
                              <br />
                              <div className='flex'>
                                <div className='text-nowrap'>※1</div>
                                <div className='text-message'>
                                  <span>
                                    CCUSの定期メンテナンス（毎週木曜日20:00～翌7:00）を除きます。
                                  </span>
                                  <span>
                                    CCUSの臨時メンテナンスが夜間に発生した場合も連携されません。
                                  </span>
                                </div>
                              </div>
                              <div className='flex'>
                                <div className='text-nowrap'>※2</div>
                                <div className='text-message'>
                                  <span>
                                    手動での連携はCCUS連携ユーザーに設定されたユーザーのみ可能です。
                                  </span>
                                </div>
                              </div>
                            </div>
                          </FormElement>
                        </FormListItem>
                      )}
                  </FormList>
                </div>
              </div>
            </div>
          </div>
          {this.showCcusConnectingDialog()}
          <Confirmation
            visible={this.state.isShowStopCcusConfirmDialog}
            rootClassName='dialog--wide FieldCcusConfirmDialog'
            title={messageUtility.getMessage('I-000-221')}
            message={messageUtility.getMessage('I-001-508')}
            confirmButtonText={'停止'}
            cancelButtonText={messageUtility.getMessage('I-000-178')}
            effectiveButtonClassName='btn--negative'
            onCancel={() => {
              this.setState({
                isShowStopCcusConfirmDialog: false,
              });
            }}
            onConfirm={this.stopCcusFieldConnection}
            multilineText={true}
          />
        </div>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/pages/FieldCcusCooperation.jsx